import type { Board, Rect, Slot } from './layout';
import { rowsOf } from './layout';
import s from './lockers.module.css';

export interface LayoutGridProps {
  cols: number;
  control: Rect;
  slots: Slot[];
  boards?: Board[];
  selectedId?: string | null;
  invalidSlotIds?: Set<string>;
  controlInvalid?: boolean;
  onSelectSlot?: (slot: Slot) => void;
  onSelectControl?: () => void;
  onSelectEmpty?: (x: number, y: number) => void;
  compact?: boolean;
}

/** Grila compartimentelor (mockup `.slot-layout`): ecranul, compartimentele și celulele goale. */
export function LayoutGrid({
  cols,
  control,
  slots,
  boards = [],
  selectedId,
  invalidSlotIds,
  controlInvalid,
  onSelectSlot,
  onSelectControl,
  onSelectEmpty,
  compact,
}: LayoutGridProps) {
  const rows = rowsOf(slots, control);
  const boardAddr = new Map(boards.map((b) => [b.id, b.address]));
  const occupied = new Set<string>();
  for (let y = control.y; y < control.y + control.h; y++)
    for (let x = control.x; x < control.x + control.w; x++) occupied.add(`${x},${y}`);
  for (const sl of slots)
    for (let y = sl.rect.y; y < sl.rect.y + sl.rect.h; y++)
      for (let x = sl.rect.x; x < sl.rect.x + sl.rect.w; x++) occupied.add(`${x},${y}`);
  const empties: { x: number; y: number }[] = [];
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < cols; x++) if (!occupied.has(`${x},${y}`)) empties.push({ x, y });

  const area = (r: Rect) => ({
    gridColumn: `${r.x + 1} / span ${r.w}`,
    gridRow: `${r.y + 1} / span ${r.h}`,
  });
  return (
    <div
      className={s.grid}
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gridAutoRows: compact ? 36 : 52,
      }}
      role="grid"
      aria-label="Layoutul compartimentelor"
    >
      <div
        className={[s.control, controlInvalid && s.invalid].filter(Boolean).join(' ')}
        style={area(control)}
        onClick={onSelectControl}
        role="gridcell"
        aria-label="Ecranul kiosk"
        title="Ecranul kiosk (controlerul)"
      >
        {!compact && <div className={s.miniScreen} />}
        {!compact && <span>ECRAN</span>}
      </div>
      {slots.map((sl) => (
        <button
          type="button"
          key={sl.id}
          className={[
            s.slot,
            sl.techState === 'blocked' && s.blocked,
            sl.techState !== 'blocked' && sl.occupancy === 'occupied' && s.occupied,
            sl.techState !== 'blocked' && sl.occupancy === 'reserved' && s.reserved,
            selectedId === sl.id && s.selected,
            invalidSlotIds?.has(sl.id) && s.invalid,
          ]
            .filter(Boolean)
            .join(' ')}
          style={area(sl.rect)}
          onClick={() => onSelectSlot?.(sl)}
          role="gridcell"
          aria-label={`Compartimentul ${sl.label}`}
          aria-selected={selectedId === sl.id}
        >
          <strong>{sl.label}</strong>
          {!compact && (
            <small>
              {sl.size} · {boardAddr.get(sl.boardId) ?? '?'}:{sl.door}
            </small>
          )}
          {sl.doorOpen && <span className={s.openLight} title="Ușă deschisă" />}
        </button>
      ))}
      {empties.map((c) => (
        <div
          key={`${c.x},${c.y}`}
          className={s.cellEmpty}
          style={area({ ...c, w: 1, h: 1 })}
          onClick={onSelectEmpty ? () => onSelectEmpty(c.x, c.y) : undefined}
          role="gridcell"
          aria-label={`Celulă liberă ${c.x + 1},${c.y + 1}`}
        />
      ))}
    </div>
  );
}

export function LayoutLegend() {
  return (
    <div className={s.legend}>
      <span>
        <i style={{ background: '#f4f9ff', border: '1px solid #c6dcf4' }} /> liber
      </span>
      <span>
        <i style={{ background: '#fff7e7', border: '1px solid #eed399' }} /> rezervat
      </span>
      <span>
        <i style={{ background: '#eaf3ff', border: '1px solid #a8c7f2' }} /> ocupat
      </span>
      <span>
        <i style={{ background: '#e9edf3', border: '1px solid #d5dce6' }} /> blocat tehnic
      </span>
      <span>
        <i style={{ background: '#273b55' }} /> ecran
      </span>
      <span>
        <i style={{ background: '#e97268', borderRadius: '50%' }} /> ușă deschisă
      </span>
    </div>
  );
}
